import {useLocalStorage} from 'platform/components';
import {Box} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useEffect, useState, useCallback, useMemo, ReactNode} from 'react';

import {getSafely, pushGtmEvent, useOnMount} from 'shared/web';

import {sourcingStorageKey} from '../../constants/sourcingStorageKey';
import {useSourcingPage} from '../../hooks/useSourcingPage';
import {SourcingType, useSourcingType} from '../../hooks/useSourcingType';
import {AllOffersPage} from '../../pages/AllOffers/AllOffersPage';
import {
  UpdateFiltersFunction,
  FiltersContextProvider,
} from '../../pages/AllOffers/components/FiltersContext/FiltersContext';
import {DEFAULT_FILTERS, FiltersType} from '../../pages/AllOffers/types/FiltersType';
import {hasSomeFilters} from '../../pages/AllOffers/utils/hasSomeFilters';
import {migrateFiltersFromOldStructure} from '../../pages/AllOffers/utils/migrateFiltersFromOldStructure';
import {UserCarvagoBuyPage} from '../../pages/UserCarvagoBuy/UserCarvagoBuyPage';
import {UserComparisonPage} from '../../pages/UserComparison/UserComparisonPage';
import {UserFavouritePage} from '../../pages/UserFavourite/UserFavouritePage';
import {UserHiddenPage} from '../../pages/UserHidden/UserHiddenPage';
import {UserToBuyPage} from '../../pages/UserToBuy/UserToBuyPage';
import {SourcingPage} from '../../types/SourcingPage';
import {isUnifiedSourcingEnabled} from '../../utils/isUnifiedSourcingEnabled';

const toSnakeCase = (value: string) =>
  value.replace(/[A-Z]/g, (letter) => `_${letter}`.toLowerCase());

function SourcingListVisitAnalytics(props: {
  sourcingPage: SourcingPage;
  sourcingType: SourcingType;
}) {
  useOnMount(() => {
    const event = `sourcing_${props.sourcingType}_list_viewed`;
    pushGtmEvent({
      event,
      event_id: event,
      params: {list_page: props.sourcingPage || 'all-offers'},
    });
  });

  return null;
}

export const SourcingListingPage = () => {
  const sourcingPage = useSourcingPage();
  const sourcingType = useSourcingType();

  const [initialFilters, saveFiltersToLocalStorage] = useLocalStorage<FiltersType>(
    sourcingStorageKey.FILTERS,
    DEFAULT_FILTERS
  );
  const migratedFilters = migrateFiltersFromOldStructure(initialFilters);
  const [filters, setFilters] = useState<FiltersType>(migratedFilters);

  useEffect(
    function persistFiltersToLocalStorage() {
      saveFiltersToLocalStorage(filters);
    },
    [saveFiltersToLocalStorage, filters]
  );

  const onUpdateFilters = useCallback<UpdateFiltersFunction>(
    (key, value) => {
      // GA4 event names are limited to 40 characters. Keep the source type in
      // the event name and expose the concrete filter as a parameter, never its value.
      const event = `sourcing_${sourcingType}_filter_changed`;
      pushGtmEvent({
        event,
        event_id: event,
        params: {filter_key: toSnakeCase(String(key))},
      });

      setFilters((filters) => {
        const currentValue = getSafely(filters, key);
        return {
          ...filters,
          [key]: typeof value === 'function' ? value(currentValue) : value,
        };
      });
    },
    [sourcingType]
  );

  const onClearFilters = useCallback(() => {
    pushGtmEvent({
      event: `sourcing_${sourcingType}_filters_cleared`,
      event_id: `sourcing_${sourcingType}_filters_cleared`,
    });
    setFilters(DEFAULT_FILTERS);
  }, [sourcingType]);

  const contextValue = useMemo(
    () => ({
      filters,
      // Derived boolean pre-computed here so consumers that only need
      // hasFilters don't subscribe to the full filters object (rerender-derived-state).
      hasFilters: hasSomeFilters(filters),
      onChange: setFilters,
      onUpdateFilters,
      onClearFilters,
    }),
    [filters, onUpdateFilters, onClearFilters]
  );

  const currentPage = match<SourcingPage, ReactNode>(sourcingPage)
    .with(SourcingPage.COMPARISON_PAGE, () => <UserComparisonPage />)
    .with(SourcingPage.CART_PAGE, () => <UserToBuyPage />)
    .with(SourcingPage.HIDDEN_PAGE, () => <UserHiddenPage />)
    .with(SourcingPage.FAVORITES_PAGE, () => <UserFavouritePage />)
    .with(SourcingPage.CARVAGO_BUY_PAGE, () => <UserCarvagoBuyPage />)
    .with(SourcingPage.DEFAULT, () => <AllOffersPage />)
    .exhaustive();

  return (
    <FiltersContextProvider value={contextValue}>
      <SourcingListVisitAnalytics
        key={`${sourcingType}:${sourcingPage}`}
        sourcingPage={sourcingPage}
        sourcingType={sourcingType}
      />
      {isUnifiedSourcingEnabled() ? (
        <Box height="100%" backgroundColor="palettes.neutral.20.100">
          {currentPage}
        </Box>
      ) : (
        currentPage
      )}
    </FiltersContextProvider>
  );
};
